// Debt-tier code mapping editor — embedded in the Settings page.
//
// The debt tier (over_20k / over_10k / below_10k) a pull qualifies for is
// derived from the response/product codes the upstream returns. This editor
// lets an operator add/edit/remove the code→tier rows and save them to the
// config table. Selection rule: highest tier wins; unlisted codes ignored; no
// match → below_10k floor. The tier-derivation + dashboard debt-mix cards read
// this mapping.

import { useCallback, useEffect, useState } from "react";

import { api, type DebtTier, type TierCode } from "../lib/api";
import { buttonStyle, Card, Pill, Skeleton, tokens } from "../lib/theme";

const TIERS: Array<{ value: DebtTier; label: string }> = [
  { value: "over_20k", label: "Over $20k (Premium)" },
  { value: "over_10k", label: "Over $10k (Mid)" },
  { value: "below_10k", label: "Under $10k (Standard)" },
];

export default function DebtTierEditor() {
  const [codes, setCodes] = useState<TierCode[] | null>(null);
  const [source, setSource] = useState<"stored" | "default" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .getDebtTiers()
      .then((d) => {
        setCodes(d.codes.slice().sort((a, b) => a.code - b.code));
        setSource(d.source);
        setError(null);
        setDirty(false);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = (idx: number, patch: Partial<TierCode>) => {
    setCodes((prev) => {
      if (!prev) return prev;
      const next = prev.slice();
      next[idx] = { ...next[idx]!, ...patch };
      return next;
    });
    setDirty(true);
  };
  const remove = (idx: number) => {
    setCodes((prev) => (prev ? prev.filter((_, i) => i !== idx) : prev));
    setDirty(true);
  };
  const add = () => {
    setCodes((prev) => [...(prev ?? []), { code: 0, range: "", tier: "below_10k" }]);
    setDirty(true);
  };

  const save = useCallback(() => {
    if (!codes) return;
    const seen = new Set<number>();
    for (const c of codes) {
      if (!Number.isInteger(c.code) || c.code <= 0) return setError(`Invalid code: ${c.code}`);
      if (seen.has(c.code)) return setError(`Duplicate code: ${c.code}`);
      seen.add(c.code);
    }
    setSaving(true);
    api
      .putDebtTiers(codes)
      .then((d) => {
        setCodes(d.codes.slice().sort((a, b) => a.code - b.code));
        setSource(d.source);
        setError(null);
        setDirty(false);
        setSavedAt(new Date());
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Save failed"))
      .finally(() => setSaving(false));
  }, [codes]);

  const inputStyle: React.CSSProperties = {
    background: tokens.color.bg2,
    color: tokens.color.fg0,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: tokens.radius.md,
    padding: `${tokens.space.xs}px ${tokens.space.sm}px`,
    fontSize: tokens.font.size.sm,
    width: "100%",
  };

  return (
    <Card
      title="Debt-tier code mapping"
      style={{ marginBottom: tokens.space.xl }}
      action={
        <div style={{ display: "flex", gap: tokens.space.sm, alignItems: "center" }}>
          {source && <Pill tone={source === "stored" ? "success" : "neutral"}>{source}</Pill>}
          {savedAt && (
            <span style={{ color: tokens.color.tealLight, fontSize: tokens.font.size.xs }}>
              Saved {savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <button type="button" onClick={add} style={buttonStyle("ghost")}>
            + Add code
          </button>
          <button type="button" onClick={save} disabled={saving || !dirty} style={buttonStyle("primary", saving || !dirty)}>
            {saving ? "Saving…" : dirty ? "Save" : "Saved"}
          </button>
        </div>
      }
    >
      <p style={{ color: tokens.color.fg2, fontSize: tokens.font.size.sm, marginTop: 0 }}>
        Each response code maps to a debt tier. When a pull returns multiple
        codes, the <strong>highest tier wins</strong> (Premium &gt; Mid &gt;
        Standard); unlisted codes are ignored; no match floors to{" "}
        <strong>Under $10k</strong>. Drives tier derivation + the dashboard
        debt-mix cards.
      </p>

      {error && (
        <div style={{ color: tokens.color.danger, marginBottom: tokens.space.sm }}>{error}</div>
      )}

      {loading && !codes ? (
        <>
          <Skeleton height={36} />
          <div style={{ height: 8 }} />
          <Skeleton height={36} />
        </>
      ) : codes ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: tokens.font.size.sm }}>
            <thead>
              <tr>
                <Th>Code</Th>
                <Th>Range / description</Th>
                <Th>Tier</Th>
                <Th align="right" />
              </tr>
            </thead>
            <tbody>
              {codes.map((c, idx) => (
                <tr key={idx}>
                  <td style={tdStyle}>
                    <input
                      type="number"
                      value={c.code || ""}
                      onChange={(e) => update(idx, { code: Number(e.target.value) })}
                      style={{ ...inputStyle, width: 100 }}
                      aria-label="Code"
                    />
                  </td>
                  <td style={tdStyle}>
                    <input
                      type="text"
                      value={c.range}
                      onChange={(e) => update(idx, { range: e.target.value })}
                      style={inputStyle}
                      aria-label="Range"
                    />
                  </td>
                  <td style={tdStyle}>
                    <select
                      value={c.tier}
                      onChange={(e) => update(idx, { tier: e.target.value as DebtTier })}
                      style={{ ...inputStyle, width: "auto", cursor: "pointer" }}
                      aria-label="Tier"
                    >
                      {TIERS.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <button type="button" onClick={() => remove(idx)} style={buttonStyle("ghost")}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Card>
  );
}

const tdStyle: React.CSSProperties = {
  padding: "8px 12px",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
};

function Th({ children, align = "left" }: { children?: React.ReactNode; align?: "left" | "right" }) {
  return (
    <th
      style={{
        textAlign: align,
        padding: "10px 12px",
        color: "rgba(255,255,255,0.5)",
        fontWeight: 600,
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      {children}
    </th>
  );
}
